import { Box, Flex, LinkBox, Tag, Text } from '@chakra-ui/react';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { useMemo } from 'react';

import { getReadingTime } from '#features/blog/utils/getReadingTime';
import { useFormatDistanceToNow } from '#features/date';
import { Image, Link, Panel } from '#features/ui';

type BlogPostPreviewProps = {
  slug: string;
  previewImage: string;
  title: string;
  tags?: string;
  date?: string;
  ogDescription?: string;
  content?: string;
  readingTime?: number;
};

export const BlogPostPreview = ({
  title,
  previewImage,
  slug,
  tags,
  date,
  ogDescription,
  content,
  readingTime: suppliedReadingTime,
}: BlogPostPreviewProps) => {
  const { _ } = useLingui();
  const formatDistanceToNow = useFormatDistanceToNow();
  const readingTime = useMemo(
    () => suppliedReadingTime ?? (content ? getReadingTime(content) : null),
    [content, suppliedReadingTime]
  );

  return (
    <Panel asChild variant="object" interactive>
      <LinkBox
        as="article"
        display="flex"
        w="100%"
        overflow="hidden"
        flexDir="column"
        transition="transform var(--chakra-durations-moderate) var(--chakra-easings-ease-out), background-color var(--chakra-durations-moderate) var(--chakra-easings-ease-out)"
        _hover={{ transform: 'translateY(-2px)' }}
        css={{
          '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
            '&:hover': { transform: 'none' },
          },
        }}
      >
        {/* Image */}
        <Box w="100%" aspectRatio="16 / 9" overflow="hidden">
          <Image
            src={previewImage}
            alt={title}
            style={{
              objectFit: 'cover',
              objectPosition: 'top center',
              width: '100%',
              height: '100%',
            }}
          />
        </Box>

        {/* Content */}
        <Flex direction="column" p={5} gap={3} flex={1}>
          {/* Tags + reading time */}
          <Flex gap={2} alignItems="center" flexWrap="wrap">
            {tags && (
              <>
                {tags
                  .split(',')
                  .map((tag) => tag.trim())
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((tag) => (
                    <Tag.Root key={tag} size="sm">
                      <Tag.Label textStyle="label">{tag}</Tag.Label>
                    </Tag.Root>
                  ))}
              </>
            )}
            {readingTime && (
              <Text textStyle="caption" color="fg.muted">
                {readingTime} {_(msg({ id: 'blog.readingTime', message: 'min read' }))}
              </Text>
            )}
          </Flex>

          {/* Title */}
          <Text as="h2" textStyle="blog.cardTitle" lineClamp={2}>
            {title}
          </Text>

          {/* Description */}
          {ogDescription && (
            <Text textStyle="blog.cardDescription" color="fg.muted" lineClamp={2}>
              {ogDescription}
            </Text>
          )}

          {/* Date */}
          <Flex mt="auto" pt={1}>
            {date && (
              <Text textStyle="caption" color="fg.muted">
                {formatDistanceToNow(date)}
              </Text>
            )}
          </Flex>
        </Flex>

        <Link to="/{-$locale}/blog/$slug" params={{ slug }} overlay aria-label={title} />
      </LinkBox>
    </Panel>
  );
};
